import { Injectable, Provider } from '@angular/core';
import { PriceFormat } from 'projects/shared/lib/components/shared/price/format/price-format.model';
import { PriceTemplateData } from 'projects/shared/lib/components/shared/price/template-data/price-template-data.model';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { EProductPriceType, ProductComplexPriceDto } from '../../INTEGRA_Shop_Client';
import { IPriceTemplateService, IPriceTemplateServiceToken } from './price-template-service.interface';

@Injectable({
  providedIn: 'root'
})
export class PriceTemplateService implements IPriceTemplateService {

  public productPriceTemplateGetByProductComplexPrice(productComplexPriceList: ProductComplexPriceDto[]): PriceTemplateData {
    let priceTemplate = this.priceTemplateGetStubByComplexPriceList(productComplexPriceList);

    this.prepareOrderPrices(priceTemplate);
    this.prepareReferencePrices(priceTemplate);

    this.updatePriceMark(priceTemplate);

    return priceTemplate;
  }


  private priceTemplateGetStubByComplexPriceList(productComplexPriceList: ProductComplexPriceDto[]): PriceTemplateData {
    let priceTemplate = new PriceTemplateData();

    productComplexPriceList.forEach(
      (productComplexPrice) => {
        let isUnitPrice = productComplexPrice.priceQuantity == 1;
        let priceFormat = new PriceFormat(productComplexPrice);

        switch (productComplexPrice.productPriceType) {

          case EProductPriceType.ListPrice:
            if (isUnitPrice) {
              priceTemplate.listUnitPrice = priceFormat;
            } else {
              priceTemplate.listPricePerPriceQuantity = priceFormat;
            }
            break;

          case EProductPriceType.CustomPrice:
            if (isUnitPrice) {
              priceTemplate.customUnitPrice = priceFormat;
            } else {
              priceTemplate.customPricePerPriceQuantity = priceFormat;
            }

            priceTemplate.hasCustomerPrice = true;
            break;

          case EProductPriceType.QuantityScalePrice:
            if (isUnitPrice) {
              priceTemplate.scaleUnitPrice = priceFormat;
            } else {
              priceTemplate.scalePricePerPriceQuantity = priceFormat;
            }

            priceTemplate.hasCustomerPrice = true;
            break;

          default:
            throw new Error("Missing implementation price template price type.");

        }
      }
    );

    let orderComplexPrice: ProductComplexPriceDto | undefined = productComplexPriceList.find(p => p.isOrderPrice == true);
    priceTemplate.orderPriceType = orderComplexPrice?.productPriceType;
    priceTemplate.orderPriceIsUnitPrice = orderComplexPrice?.priceQuantity == 1;

    return priceTemplate;
  }

  private prepareOrderPrices(priceTemplate: PriceTemplateData): void {

    switch (priceTemplate.orderPriceType) {

      case undefined:
        return;

      case EProductPriceType.ListPrice:
        priceTemplate.orderPricePerPriceQuantity = priceTemplate.listPricePerPriceQuantity;
        priceTemplate.orderUnitPrice = priceTemplate.listUnitPrice;
        break;

      case EProductPriceType.CustomPrice:
        priceTemplate.orderPricePerPriceQuantity = priceTemplate.customPricePerPriceQuantity;
        priceTemplate.orderUnitPrice = priceTemplate.customUnitPrice;
        break;

      case EProductPriceType.QuantityScalePrice:
        priceTemplate.orderPricePerPriceQuantity = priceTemplate.scalePricePerPriceQuantity;
        priceTemplate.orderUnitPrice = priceTemplate.scaleUnitPrice;
        break;

      default:
        throw new Error("Missing implementation for complex price to template mapping.");

    }

    this.prepareOrderUnitPrice(priceTemplate);
    this.prepareOrdeScalePrice(priceTemplate);
  }

  private prepareOrderUnitPrice(priceTemplate: PriceTemplateData): void {
    let orderUnitPriceWithoutDiscounts = priceTemplate.orderUnitPrice?.priceWithoutDiscounts;
    if (priceTemplate.orderUnitPrice == undefined) {
      return;
    }

    if (orderUnitPriceWithoutDiscounts?.netPrice != undefined && orderUnitPriceWithoutDiscounts.netPrice > 0) {
      priceTemplate.orderUnitPrice.isNetPriceWithoutDiscountsGreaterThan0 = true;
    }

    if (orderUnitPriceWithoutDiscounts?.brutPrice != undefined && orderUnitPriceWithoutDiscounts.brutPrice > 0) {
      priceTemplate.orderUnitPrice.isBrutPriceWithoutDiscountsGreaterThan0 = true;
    }

    let shouldDisplayNetPrices = ENVIRONMENT.price.displayNet;
    let shouldDisplayBrutPrices = ENVIRONMENT.price.displayBrut;

    priceTemplate.orderUnitPrice.shouldDisplayPriceOnRequest =
      (shouldDisplayNetPrices && priceTemplate.orderUnitPrice.isNetPriceWithoutDiscountsGreaterThan0 != true)
      || (shouldDisplayBrutPrices && priceTemplate.orderUnitPrice.isBrutPriceWithoutDiscountsGreaterThan0 != true);
  }

  private prepareOrdeScalePrice(priceTemplate: PriceTemplateData): void {
    let orderScalePriceWithoutDiscounts = priceTemplate.orderPricePerPriceQuantity?.priceWithoutDiscounts;
    if (priceTemplate.orderPricePerPriceQuantity == undefined) {
      return;
    }

    if (orderScalePriceWithoutDiscounts?.netPrice != undefined && orderScalePriceWithoutDiscounts.netPrice > 0) {
      priceTemplate.orderPricePerPriceQuantity.isNetPriceWithoutDiscountsGreaterThan0 = true;
    }

    if (orderScalePriceWithoutDiscounts?.brutPrice != undefined && orderScalePriceWithoutDiscounts.brutPrice > 0) {
      priceTemplate.orderPricePerPriceQuantity.isBrutPriceWithoutDiscountsGreaterThan0 = true;
    }

    let shouldDisplayNetPrices = ENVIRONMENT.price.displayNet;
    let shouldDisplayBrutPrices = ENVIRONMENT.price.displayBrut;

    priceTemplate.orderPricePerPriceQuantity.shouldDisplayPriceOnRequest =
      (shouldDisplayNetPrices && priceTemplate.orderPricePerPriceQuantity.isNetPriceWithoutDiscountsGreaterThan0 != true)
      || (shouldDisplayBrutPrices && priceTemplate.orderPricePerPriceQuantity.isBrutPriceWithoutDiscountsGreaterThan0 != true);
  }

  private prepareReferencePrices(priceTemplate: PriceTemplateData): void {
    priceTemplate.referencePriceType = EProductPriceType.ListPrice;
    priceTemplate.referencePriceIsUnitPrice = priceTemplate.orderPriceIsUnitPrice == true || priceTemplate.listPricePerPriceQuantity == undefined;

    let listPriceWithDiscounts = priceTemplate.listPricePerPriceQuantity?.priceWithDiscounts;
    if (listPriceWithDiscounts == undefined || priceTemplate.orderPriceIsUnitPrice == true) {
      listPriceWithDiscounts = priceTemplate.listUnitPrice?.priceWithDiscounts;
    }

    let orderPriceWithoutDiscounts = priceTemplate.orderPricePerPriceQuantity?.priceWithoutDiscounts;
    if (orderPriceWithoutDiscounts == undefined || priceTemplate.orderPriceIsUnitPrice == true) {
      orderPriceWithoutDiscounts = priceTemplate.orderUnitPrice?.priceWithoutDiscounts;
    }

    priceTemplate.hasDifferentListAndOrderPrices = listPriceWithDiscounts?.netPrice != orderPriceWithoutDiscounts?.netPrice;
    if (listPriceWithDiscounts?.netPrice != undefined && orderPriceWithoutDiscounts?.netPrice != undefined) {
      priceTemplate.listPriceIsHigherThanOrderPrice = listPriceWithDiscounts.netPrice > orderPriceWithoutDiscounts.netPrice;
    }

  }

  private updatePriceMark(priceTemplate: PriceTemplateData) {
    if (priceTemplate.orderPriceIsUnitPrice) {
      priceTemplate.orderPriceMark = priceTemplate.orderUnitPrice?.priceMark;
    } else {
      priceTemplate.orderPriceMark = priceTemplate.orderPricePerPriceQuantity?.priceMark;
    }
  }

}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const PriceTemplateServiceProvider: Provider[] = [
  { provide: IPriceTemplateServiceToken, useExisting: PriceTemplateService }
];
